import { createSilentGraph, mixGraphs } from "@/client/core/audio/Audio";
import {
    createPreviewPlayerFactory,
    type OverlayHandle,
    type SetOverlayGraph,
} from "@/client/core/audio/PreviewPlayer";
import { createTestToneGraph } from "@/client/core/audio/test-tone.audio";
import type { LabsElementaryGraph } from "@/client/core/platform-types/labs.api";
import type {
    CoreActions,
    CoreStreams,
    ElementaryService,
    GetTransportState,
    OpenExternalLink,
    ResourcesService,
    ShowUserNotification,
} from "@/client/core/Core.api";
import { createAddFiles } from "@/client/core/services/add-files.service";

export const createCoreActions = (
    elementaryService: ElementaryService,
    resourcesService: ResourcesService,
    streams: CoreStreams,
    showUserNotification: ShowUserNotification,
    openExternalLink: OpenExternalLink,
    getTransportState: GetTransportState,
): CoreActions => {
    // The host renders ONE graph, so auditioning a file used to replace whatever
    // the app was rendering — and `stopAudio` then left silence behind, so an app
    // that drives a persistent graph (a sampler holding its pad voices) went quiet
    // until something happened to re-render it. Core keeps the two apart and mixes
    // them: the app owns its layer, previews and the test tone come and go above it.
    let appGraph: LabsElementaryGraph = createSilentGraph();
    let overlayGraph: LabsElementaryGraph | undefined;

    const renderLayers = () => {
        const graph = overlayGraph === undefined ? appGraph : mixGraphs(appGraph, overlayGraph);
        // The plugin renderer injects transport meters by writing to nodes[0]. Core keeps
        // these layers across renders, so hand over a copy: otherwise every overlay
        // transition would stack another set of meters onto the graph we are holding.
        elementaryService.setGraph({
            nodes: [graph.nodes[0], graph.nodes[1]],
            resourceIDs: graph.resourceIDs,
        });
    };

    const setAudioGraph: CoreActions["setAudioGraph"] = (graph) => {
        appGraph = graph;
        renderLayers();
    };

    // Only one overlay sounds at a time, so a later one supersedes whatever was
    // there. Each caller gets a `clear` bound to its own turn: once superseded it
    // does nothing, which stops a finished preview from silencing the test tone
    // that replaced it.
    let overlayTurn = 0;

    const setOverlayGraph: SetOverlayGraph = (graph) => {
        overlayGraph = graph;
        overlayTurn += 1;
        const turn = overlayTurn;
        renderLayers();
        return {
            clear: () => {
                if (turn !== overlayTurn) {
                    return;
                }
                overlayGraph = undefined;
                renderLayers();
            },
        };
    };

    let testToneOverlay: OverlayHandle | undefined;
    const updateTestTone = (isPlaying: boolean) => {
        if (isPlaying) {
            testToneOverlay = setOverlayGraph(createTestToneGraph());
            return;
        }
        testToneOverlay?.clear();
        testToneOverlay = undefined;
    };

    // Stops whatever Core is auditioning, whoever started it. The app's own graph
    // is not Core's to silence.
    const stopAudio: CoreActions["stopAudio"] = () => {
        overlayGraph = undefined;
        overlayTurn += 1;
        renderLayers();
    };

    const createPreviewPlayer = createPreviewPlayerFactory(
        resourcesService.getAudioResource,
        setOverlayGraph,
    );

    const addFiles = createAddFiles(streams, resourcesService.addFiles);

    const actions: CoreActions = {
        setAudioGraph,
        startTestTone: () => updateTestTone(true),
        stopTestTone: () => updateTestTone(false),
        stopAudio,
        deleteResources: resourcesService.deleteResources,
        updateResourceMetadata: resourcesService.updateResourceMetadata,
        requestAllResources: resourcesService.requestAllResources,
        getAudioResource: resourcesService.getAudioResource,
        getMidiResource: resourcesService.getMidiResource,
        getJsonResource: resourcesService.getJsonResource,
        revealResourceOnDisk: resourcesService.revealOnDisk,
        createPreviewPlayer,
        addFiles,
        openExternalLink,
        showUserNotification,
        getTransportState,
    };

    return actions;
};
